import React, { useState } from 'react';
import { gql, useApolloClient } from '@apollo/client';
import type { DocumentNode } from 'graphql';
import { Activity, CheckCircle2, Play, RefreshCw, XCircle } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useUser } from '@/store/useUser';

type DiagnosticProbe = {
  key: string;
  label: string;
  category: string;
  statement: string;
  durationMs: number;
  success: boolean;
  statusMessage: string;
};

type DatabaseDiagnosticResult = {
  measuredAt: string;
  successful: boolean;
  failedProbeCount: number;
  resolverDurationMs: number;
  databaseDurationMs: number;
  serverOverheadMs: number;
  connectionStrategy: string;
  probes: DiagnosticProbe[];
};

type AwsDiagnosticResult = {
  measuredAt: string;
  successful: boolean;
  durationMs: number;
  region: string | null;
  account: string | null;
  arn: string | null;
  statusMessage: string;
};

type ServerMemory = {
  rssBytes: number;
  heapTotalBytes: number;
  heapUsedBytes: number;
  externalBytes: number;
  arrayBuffersBytes: number;
};

type ServerStatus = {
  userAuthenticated: boolean;
  version: string | null;
  measuredAt: string;
  uptimeSeconds: number;
  memory: ServerMemory;
};

type OperationCheck = {
  key: string;
  label: string;
  scope: string;
  durationMs: number | null;
  state: 'passed' | 'failed' | 'skipped';
  statusMessage: string;
};

type DiagnosticsRun = {
  measuredAt: string;
  browserDurationMs: number;
  checks: OperationCheck[];
  aws?: AwsDiagnosticResult;
  database?: DatabaseDiagnosticResult;
  server?: ServerStatus;
};

type TimedQueryResult<TData> = {
  success: boolean;
  durationMs: number;
  data?: TData;
};

type CheckInspection = {
  success: boolean;
  message: string;
};

type CurrentUserData = {
  getCurrentUserData: { id: number } | null;
};

type StatusData = {
  status: ServerStatus | null;
};

type UserListData = {
  listUsers: { totalCount: number } | null;
};

type ParticipantData = {
  getParticipantByODRNumberAdmin: { id: number } | null;
};

type DatabaseDiagnosticsData = {
  databaseDiagnostics: DatabaseDiagnosticResult;
};

type AwsDiagnosticsData = {
  awsDiagnostics: AwsDiagnosticResult;
};

export const CURRENT_USER_DIAGNOSTIC_QUERY = gql`
  query DiagnosticsCurrentUser {
    getCurrentUserData {
      id
    }
  }
`;

export const STATUS_DIAGNOSTIC_QUERY = gql`
  query DiagnosticsStatus {
    status {
      userAuthenticated
      version
      measuredAt
      uptimeSeconds
      memory {
        rssBytes
        heapTotalBytes
        heapUsedBytes
        externalBytes
        arrayBuffersBytes
      }
    }
  }
`;

export const USER_LIST_DIAGNOSTIC_QUERY = gql`
  query DiagnosticsUserList($filters: FilterOptions!) {
    listUsers(filters: $filters) {
      totalCount
    }
  }
`;

export const PARTICIPANT_DIAGNOSTIC_QUERY = gql`
  query DiagnosticsParticipant($odrNumber: Int!) {
    getParticipantByODRNumberAdmin(odrnumber: $odrNumber) {
      id
    }
  }
`;

export const DATABASE_DIAGNOSTICS_QUERY = gql`
  query DatabaseDiagnostics {
    databaseDiagnostics {
      measuredAt
      successful
      failedProbeCount
      resolverDurationMs
      databaseDurationMs
      serverOverheadMs
      connectionStrategy
      probes {
        key
        label
        category
        statement
        durationMs
        success
        statusMessage
      }
    }
  }
`;

export const AWS_DIAGNOSTICS_QUERY = gql`
  query AwsDiagnostics {
    awsDiagnostics {
      measuredAt
      successful
      durationMs
      region
      account
      arn
      statusMessage
    }
  }
`;

const formatMilliseconds = (value: number | null | undefined) => {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'Not measured';
  return `${value.toFixed(3)} ms`;
};

const formatBytes = (value: number | null | undefined) => {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'Unavailable';
  return `${(value / 1024 / 1024).toFixed(1)} MiB`;
};

const formatUptime = (value: number | null | undefined) => {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'Unavailable';
  const totalMinutes = Math.floor(value / 60);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const MetricCard = ({ label, value, description }: { label: string; value: string; description: string }) => (
  <Card>
    <CardHeader className="pb-2">
      <CardDescription>{label}</CardDescription>
      <CardTitle className="text-xl tabular-nums">{value}</CardTitle>
    </CardHeader>
    <CardContent className="text-xs text-muted-foreground">{description}</CardContent>
  </Card>
);

const runTimedQuery = async <TData,>(
  client: ReturnType<typeof useApolloClient>,
  query: DocumentNode,
  variables?: Record<string, unknown>,
): Promise<TimedQueryResult<TData>> => {
  const startedAt = performance.now();
  try {
    const result = await client.query<TData>({ query, variables, fetchPolicy: 'no-cache' });
    return {
      success: true,
      durationMs: performance.now() - startedAt,
      data: result.data,
    };
  } catch (_error) {
    return {
      success: false,
      durationMs: performance.now() - startedAt,
    };
  }
};

const buildOperationCheck = <TData,>(
  key: string,
  label: string,
  scope: string,
  result: TimedQueryResult<TData>,
  inspect: (data: TData) => CheckInspection,
): OperationCheck => {
  if (!result.success || !result.data) {
    return {
      key,
      label,
      scope,
      durationMs: result.durationMs,
      state: 'failed',
      statusMessage: 'Request failed without exposing response details',
    };
  }

  const inspection = inspect(result.data);
  return {
    key,
    label,
    scope,
    durationMs: result.durationMs,
    state: inspection.success ? 'passed' : 'failed',
    statusMessage: inspection.message,
  };
};

const statusIcon = (state: OperationCheck['state']) => {
  if (state === 'passed') return <CheckCircle2 className="h-4 w-4 text-[var(--semantic-success)]" />;
  if (state === 'failed') return <XCircle className="h-4 w-4 text-destructive" />;
  return <span className="h-4 w-4 rounded-full border border-muted-foreground" aria-hidden="true" />;
};

export const DatabaseDiagnosticsPage: React.FC = () => {
  const client = useApolloClient();
  const { recentParticipants } = useUser();
  const [running, setRunning] = useState(false);
  const [run, setRun] = useState<DiagnosticsRun | null>(null);

  const handleRun = async () => {
    const startedAt = performance.now();
    const recentOdrNumber = recentParticipants[0]?.odrNumber;
    setRunning(true);
    setRun(null);

    const participantRequest = recentOdrNumber
      ? runTimedQuery<ParticipantData>(client, PARTICIPANT_DIAGNOSTIC_QUERY, { odrNumber: recentOdrNumber })
      : Promise.resolve<TimedQueryResult<ParticipantData> | null>(null);

    try {
      const [currentUserResult, statusResult, userListResult, databaseResult, awsResult, participantResult] = await Promise.all([
        runTimedQuery<CurrentUserData>(client, CURRENT_USER_DIAGNOSTIC_QUERY),
        runTimedQuery<StatusData>(client, STATUS_DIAGNOSTIC_QUERY),
        runTimedQuery<UserListData>(client, USER_LIST_DIAGNOSTIC_QUERY, {
          filters: {
            limit: 1,
            offset: 0,
            showDisabled: true,
            showLocked: true,
            sortBy: 'name',
            sortDirection: 'asc',
          },
        }),
        runTimedQuery<DatabaseDiagnosticsData>(client, DATABASE_DIAGNOSTICS_QUERY),
        runTimedQuery<AwsDiagnosticsData>(client, AWS_DIAGNOSTICS_QUERY),
        participantRequest,
      ]);

      const checks: OperationCheck[] = [
        buildOperationCheck('current-user', 'Current user', 'Authentication', currentUserResult, (data) => ({
          success: Boolean(data.getCurrentUserData?.id),
          message: data.getCurrentUserData?.id
            ? 'Authenticated user context returned'
            : 'No authenticated user context returned',
        })),
        buildOperationCheck('server-status', 'General server status', 'Server', statusResult, (data) => ({
          success: data.status?.userAuthenticated === true,
          message: data.status?.userAuthenticated
            ? `Server responded with version ${data.status.version || 'unavailable'}`
            : 'Server did not confirm an authenticated request',
        })),
        buildOperationCheck('user-list', 'User list', 'Application query', userListResult, (data) => ({
          success: Boolean(data.listUsers),
          message: data.listUsers
            ? `User list completed with ${data.listUsers.totalCount} total accounts`
            : 'User list did not return a result',
        })),
        buildOperationCheck('database', 'Database diagnostics', 'Database', databaseResult, (data) => ({
          success: data.databaseDiagnostics.successful,
          message: data.databaseDiagnostics.successful
            ? `${data.databaseDiagnostics.probes.length} read-only probes completed`
            : `${data.databaseDiagnostics.failedProbeCount} database probes failed`,
        })),
        buildOperationCheck('aws-identity', 'AWS caller identity', 'AWS', awsResult, (data) => ({
          success: data.awsDiagnostics.successful,
          message: data.awsDiagnostics.statusMessage,
        })),
      ];

      if (participantResult) {
        checks.push(buildOperationCheck(
          'participant-record',
          'Recent client record',
          'Application query',
          participantResult,
          (data) => ({
            success: Boolean(data.getParticipantByODRNumberAdmin?.id),
            message: data.getParticipantByODRNumberAdmin?.id
              ? 'One recent client record returned without displaying record data'
              : 'The recent client record was not returned',
          }),
        ));
      }

      if (!participantResult) {
        checks.push({
          key: 'participant-record',
          label: 'Recent client record',
          scope: 'Application query',
          durationMs: null,
          state: 'skipped',
          statusMessage: 'No recent client is available for this account',
        });
      }

      setRun({
        measuredAt: new Date().toISOString(),
        browserDurationMs: performance.now() - startedAt,
        checks,
        aws: awsResult.success ? awsResult.data?.awsDiagnostics : undefined,
        database: databaseResult.success ? databaseResult.data?.databaseDiagnostics : undefined,
        server: statusResult.success ? statusResult.data?.status || undefined : undefined,
      });
    } finally {
      setRunning(false);
    }
  };

  const failedCheckCount = run?.checks.filter((check) => check.state === 'failed').length || 0;
  const skippedCheckCount = run?.checks.filter((check) => check.state === 'skipped').length || 0;
  const passedCheckCount = run?.checks.filter((check) => check.state === 'passed').length || 0;
  const databaseCheck = run?.checks.find((check) => check.key === 'database');
  const outsideResolverMs = run?.database && databaseCheck?.durationMs !== null && databaseCheck?.durationMs !== undefined
    ? Math.max(0, databaseCheck.durationMs - run.database.resolverDurationMs)
    : null;

  return (
    <div className="space-y-4 p-2">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              System Diagnostics
            </CardTitle>
            <CardDescription className="mt-2 max-w-3xl">
              Run authenticated application, server, client record, user-list, and database checks with browser and
              server timing, including the AWS identity used by the server. Client record contents, user account names,
              credentials, and raw errors are not displayed.
            </CardDescription>
          </div>
          <Button id="system-diagnostics-run" type="button" onClick={handleRun} disabled={running}>
            {running ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
            {running ? 'Running' : run ? 'Run again' : 'Run diagnostics'}
          </Button>
        </CardHeader>
        {run && (
          <CardContent className="flex flex-wrap items-center gap-2 text-sm">
            <Badge
              variant={failedCheckCount === 0 ? 'default' : 'destructive'}
              className={failedCheckCount === 0 ? 'bg-[var(--semantic-success)] text-[var(--surface-panel-solid)] hover:bg-[var(--semantic-success)]' : undefined}
            >
              {failedCheckCount === 0 ? 'All available checks passed' : `${failedCheckCount} checks failed`}
            </Badge>
            <span className="text-muted-foreground">{passedCheckCount} passed</span>
            {skippedCheckCount > 0 && <span className="text-muted-foreground">{skippedCheckCount} skipped</span>}
            <span className="text-muted-foreground">Measured {new Date(run.measuredAt).toLocaleString()}</span>
          </CardContent>
        )}
      </Card>

      {!run && !running && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Diagnostics run only when you select Run diagnostics.
          </CardContent>
        </Card>
      )}

      {run && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCard
              label="Full browser run"
              value={formatMilliseconds(run.browserDurationMs)}
              description="Elapsed time until all available diagnostic requests completed."
            />
            <MetricCard
              label="Server version"
              value={run.server?.version || 'Unavailable'}
              description="Application version reported by the server status operation."
            />
            <MetricCard
              label="Server process uptime"
              value={formatUptime(run.server?.uptimeSeconds)}
              description="Elapsed time since this server process started."
            />
            <MetricCard
              label="Process RSS"
              value={formatBytes(run.server?.memory.rssBytes)}
              description="Resident memory currently held by this server process."
            />
            <MetricCard
              label="Heap used"
              value={formatBytes(run.server?.memory.heapUsedBytes)}
              description="JavaScript heap memory currently in use."
            />
            <MetricCard
              label="Heap allocated"
              value={formatBytes(run.server?.memory.heapTotalBytes)}
              description="JavaScript heap memory currently allocated to the process."
            />
            <MetricCard
              label="External memory"
              value={formatBytes(run.server?.memory.externalBytes)}
              description="Memory used by native objects associated with JavaScript values."
            />
            <MetricCard
              label="Array buffers"
              value={formatBytes(run.server?.memory.arrayBuffersBytes)}
              description="Memory allocated for ArrayBuffer and shared buffer data."
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Application checks</CardTitle>
              <CardDescription>Each duration is measured in the browser across the complete GraphQL request.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Check</TableHead>
                      <TableHead>Scope</TableHead>
                      <TableHead className="text-right">Browser duration</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {run.checks.map((check) => (
                      <TableRow key={check.key}>
                        <TableCell className="font-medium">{check.label}</TableCell>
                        <TableCell>{check.scope}</TableCell>
                        <TableCell className="text-right font-mono tabular-nums">
                          {formatMilliseconds(check.durationMs)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {statusIcon(check.state)}
                            <span>{check.statusMessage}</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {run.aws && (
            <Card>
              <CardHeader>
                <CardTitle>AWS caller identity</CardTitle>
                <CardDescription>
                  Identity returned by AWS Security Token Service for the credentials resolved by the server.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-auto rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Region</TableHead>
                        <TableHead>Account</TableHead>
                        <TableHead>Caller ARN</TableHead>
                        <TableHead className="text-right">STS duration</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell>{run.aws.region || 'Unavailable'}</TableCell>
                        <TableCell className="font-mono">{run.aws.account || 'Unavailable'}</TableCell>
                        <TableCell><code className="whitespace-nowrap text-xs">{run.aws.arn || 'Unavailable'}</code></TableCell>
                        <TableCell className="text-right font-mono tabular-nums">
                          {formatMilliseconds(run.aws.durationMs)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {run.aws.successful
                              ? <CheckCircle2 className="h-4 w-4 text-[var(--semantic-success)]" />
                              : <XCircle className="h-4 w-4 text-destructive" />}
                            <span>{run.aws.statusMessage}</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {run.database && (
            <>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                <MetricCard
                  label="Database request"
                  value={formatMilliseconds(databaseCheck?.durationMs)}
                  description="Browser round trip for the database diagnostic operation."
                />
                <MetricCard
                  label="Resolver total"
                  value={formatMilliseconds(run.database.resolverDurationMs)}
                  description="Time measured inside the database diagnostic resolver."
                />
                <MetricCard
                  label="Database queries"
                  value={formatMilliseconds(run.database.databaseDurationMs)}
                  description="Sum of the sequential database probes."
                />
                <MetricCard
                  label="Server overhead"
                  value={formatMilliseconds(run.database.serverOverheadMs)}
                  description="Resolver time not spent awaiting database queries."
                />
                <MetricCard
                  label="Outside resolver estimate"
                  value={formatMilliseconds(outsideResolverMs)}
                  description="Client, network, GraphQL, authorization, and response overhead."
                />
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Database probe timings</CardTitle>
                  <CardDescription>
                    Fixed read-only probes run sequentially against the existing injected pool-backed client.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-auto rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Probe</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead>Statement</TableHead>
                          <TableHead className="text-right">Duration</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {run.database.probes.map((probe) => (
                          <TableRow key={probe.key}>
                            <TableCell className="font-medium">{probe.label}</TableCell>
                            <TableCell className="capitalize">{probe.category}</TableCell>
                            <TableCell>
                              <code className="whitespace-nowrap rounded bg-muted px-2 py-1 text-xs">{probe.statement}</code>
                            </TableCell>
                            <TableCell className="text-right font-mono tabular-nums">
                              {formatMilliseconds(probe.durationMs)}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {probe.success
                                  ? <CheckCircle2 className="h-4 w-4 text-[var(--semantic-success)]" />
                                  : <XCircle className="h-4 w-4 text-destructive" />}
                                <span>{probe.statusMessage}</span>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          <Card>
            <CardHeader>
              <CardTitle>How to read the result</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm text-muted-foreground">
              <p>Slow application checks with fast database probes point toward GraphQL, authorization, serialization, or operation-specific work.</p>
              <p>A slow first SELECT 1 followed by a fast repeat points toward idle connection acquisition or warm-up.</p>
              <p>Fast SELECT 1 probes with slower table probes point toward table, index, lock, or cache behavior.</p>
              <p>Memory is a point-in-time reading for the server process handling the status request, not the whole host.</p>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};
